import { faColumns, faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown, Form, Pagination, Table } from "react-bootstrap";

import { DataMatchPersonSnapshot } from "../../../../models/DataMatchResult";
import { smartCompare } from "../../../../app/common/helper/SortUtils";
import { getCentralColumnValue } from "./column-options";
import { centralRowKey } from "./data-match-utils";
import {
    CENTRAL_UNMATCHED_COLUMNS,
    exportUnmatchedCsv,
    matchesNameSearch,
    nextSortState,
    type SortState,
} from "./data-match-helpers";
import { SortIndicator } from "./MatchedRecordsSection";

const UnmatchedCentralPanel = memo(function UnmatchedCentralPanel({
    rows,
    selectedRowKey,
    onRowSelect,
}: {
    rows: DataMatchPersonSnapshot[] | null;
    selectedRowKey: string | null;
    onRowSelect: (row: DataMatchPersonSnapshot) => void;
}) {
    const columns = CENTRAL_UNMATCHED_COLUMNS;
    const [visibility, setVisibility] = useState<Record<string, boolean>>(() =>
        Object.fromEntries(columns.map((c) => [c.key, true]))
    );
    const [sort, setSort] = useState<SortState>(null);
    const [searchText, setSearchText] = useState('');

    const visibleCols = useMemo(() => columns.filter((c) => visibility[c.key] !== false), [columns, visibility]);

    const toggleVis = (key: string, checked: boolean) => {
        setVisibility((prev) => {
            if (!checked && Object.values(prev).filter(Boolean).length <= 1) return prev;
            return { ...prev, [key]: checked };
        });
    };

    const filteredRows = useMemo(() => {
        if (!rows) return rows;
        if (!searchText.trim()) return rows;
        return rows.filter((r) => matchesNameSearch(r, searchText.trim(), getCentralColumnValue));
    }, [rows, searchText]);

    const sortedRows = useMemo(() => {
        if (!filteredRows || !sort) return filteredRows;
        const col = columns.find((c) => c.key === sort.column);
        if (!col) return filteredRows;
        const decorated = filteredRows.map((r, i) => ({ r, i, sortKey: col.getValue(r) }));
        decorated.sort((a, b) => {
            const cmp = smartCompare(a.sortKey, b.sortKey);
            return sort.direction === 'asc' ? cmp : -cmp;
        });
        return decorated.map((d) => d.r);
    }, [columns, filteredRows, sort]);

    const PAGE_SIZE = 100;
    const [page, setPage] = useState(0);
    const centralScrollRef = useRef<HTMLDivElement>(null);
    useEffect(() => { setPage(0); }, [sort, searchText]);
    useEffect(() => { centralScrollRef.current?.scrollTo(0, 0); }, [page]);
    const totalPages = Math.ceil((sortedRows ?? []).length / PAGE_SIZE);
    const pagedRows = useMemo(
        () => (sortedRows ?? []).slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
        [sortedRows, page]
    );

    return (
        <div className="data-match-panel data-match-panel--central">
            <div className="data-match-panel__head data-match-panel__head--split">
                <span>MIS Central (left) | Unmatched ({rows ? rows.length : "—"})</span>
                {rows && rows.length > 0 && (
                    <div className="d-flex align-items-center gap-2">
                        <Form.Control
                            type="text"
                            size="sm"
                            placeholder="Search by name…"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="data-match-search-input"
                            aria-label="Search unmatched MIS Central records by name"
                        />
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            title="Export CSV"
                            onClick={() => { const d = new Date(); const ts = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}_${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}${String(d.getSeconds()).padStart(2, '0')}`; exportUnmatchedCsv(rows, columns, `unmatched_mis_central_${ts}.csv`); }}
                        >
                            <FontAwesomeIcon icon={faDownload} />
                        </Button>
                        <Dropdown autoClose="outside" align="end">
                            <Dropdown.Toggle variant="light" size="sm" className="data-match-cols-btn border" id="dropdown-central-unmatched-cols" title="Columns">
                                <FontAwesomeIcon icon={faColumns} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="data-match-column-picker-menu shadow-sm">
                                {columns.map((c) => (
                                    <div key={c.key} className="px-3 py-1">
                                        <Form.Check
                                            type="checkbox"
                                            id={`cu-col-${c.key}`}
                                            label={c.label}
                                            checked={visibility[c.key] !== false}
                                            onChange={(e) => toggleVis(c.key, e.target.checked)}
                                        />
                                    </div>
                                ))}
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                )}
            </div>
            <div className="data-match-panel__body p-0">
                {!rows ? (
                    <p className="p-3 mb-0 text-muted small">Run a data match to see unmatched central records.</p>
                ) : rows.length === 0 ? (
                    <p className="p-3 mb-0 text-muted small">No unmatched central records.</p>
                ) : (
                    <>
                        <div ref={centralScrollRef} className="data-match-table-scroll" role="region" aria-label="MIS Central unmatched records">
                            <Table size="sm" className="mb-0 data-match-unmatched-table">
                                <thead>
                                    <tr>
                                        {visibleCols.map((c) => (
                                            <th
                                                key={c.key}
                                                className="text-nowrap small data-match-sortable-th"
                                                onClick={() => setSort((s) => nextSortState(s, c.key))}
                                            >
                                                <span className="data-match-th-label" title={c.label}>{c.label}</span>
                                                <SortIndicator sort={sort} column={c.key} />
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedRows.map((r, i) => {
                                        const rk = centralRowKey(r);
                                        const selected = selectedRowKey != null && selectedRowKey === rk;
                                        return (
                                            <tr
                                                key={rk || `c-${page * PAGE_SIZE + i}`}
                                                className={`data-match-unmatched-tr${selected ? " data-match-unmatched-tr--selected" : ""}`}
                                                onClick={() => onRowSelect(r)}
                                                tabIndex={0}
                                                role="button"
                                                aria-pressed={selected}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        onRowSelect(r);
                                                    }
                                                }}
                                            >
                                                {visibleCols.map((c) => {
                                                    const val = c.getValue(r);
                                                    return <td key={c.key} title={val}>{val}</td>;
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                        {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top" style={{ fontSize: '0.8125rem' }}>
                                <span className="text-muted">
                                    Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, (sortedRows ?? []).length)} of {(sortedRows ?? []).length}
                                </span>
                                <Pagination size="sm" className="mb-0">
                                    <Pagination.Prev onClick={() => setPage((p) => p - 1)} disabled={page === 0} />
                                    <Pagination.Next onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages - 1} />
                                </Pagination>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
});

export default UnmatchedCentralPanel;
